import { useState, useEffect } from 'react';

import useForgotPasswordError from './useForgotPasswordError';
import { useEnvStore } from '../../../store/models';
import { RecoveryState } from '../../../utils/constants';

// 180 seconds
const RESEND_COUNTDOWN_SECONDS = 180;

function decrementCountdown(c) {
  return c <= 1 ? 0 : c - 1;
}

const EMAIL_DELIVERY_MESSAGE = 'If you have a registered email address, a recovery code has been sent to your inbox. Please enter the code below and set a new password.';
const PHONE_DELIVERY_MESSAGE = 'If you have a registered phone number, a recovery code has been sent to you via text message. Please enter the code below and set a new password.';

function getDeliveryMessage(deliveryMedium) {
  if (deliveryMedium === 'EMAIL') {
    return EMAIL_DELIVERY_MESSAGE;
  }
  if (deliveryMedium === 'SMS') {
    return PHONE_DELIVERY_MESSAGE;
  }
  return EMAIL_DELIVERY_MESSAGE;
}

function useRecoverCodeState () {
  const envStore = useEnvStore();
  const recoveryStatus = envStore.recoveryState?.status;
  const codeDeliveryDetails = envStore.recoveryState?.codeDeliveryDetails;
  const isVerifying = recoveryStatus === RecoveryState.VERIFY_RECOVERY_CODE;
  const isSending = recoveryStatus === RecoveryState.RECOVERY_CODE_SENDING;
  const deliveryMessage = getDeliveryMessage(codeDeliveryDetails?.deliveryMedium);

  const [code, setCode] = useState('');
  const [pass, setPassword] = useState('');

  const [showPassword, setShowPassword] = useState(false);

  const [input, setInput] = useState({ code, password: pass });

  const [resendCountdown, setResendCountdown] = useState(RESEND_COUNTDOWN_SECONDS);

  const { error } = useForgotPasswordError({ input });

  useEffect(() => {
    setInput({
      code,
      password: pass,
    });
  }, [code, pass]);

  useEffect(() => {
    if (resendCountdown <= 0) return;
    const timer = setTimeout(() => {
      setResendCountdown(c => decrementCountdown(c));
    }, 1000);
    return () => clearTimeout(timer);
  }, [resendCountdown]);

  const resetResendCountdown = () => {
    setResendCountdown(RESEND_COUNTDOWN_SECONDS);
  };

  return {
    code,
    setCode,
    pass,
    setPassword,
    showPassword,
    setShowPassword,
    resendCountdown,
    resetResendCountdown,
    isVerifying,
    isSending,
    error,
    codeDeliveryDetails,
    deliveryMessage,
  };
}

export default useRecoverCodeState;
